import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { router } from "@inertiajs/react"
import { useState } from "react"
import type { Column, ColumnCategory } from "../types"

const CATEGORY_LABELS: Record<ColumnCategory, string> = {
  todo: "Not started",
  in_progress: "In progress",
  done: "Done — stamps tickets complete",
}

interface ColumnDialogProps {
  boardKey: string
  column: Column | null
  onOpenChange: (open: boolean) => void
}

export function ColumnDialog({ boardKey, column, onOpenChange }: ColumnDialogProps) {
  const [name, setName] = useState(column?.name ?? "")
  const [category, setCategory] = useState<ColumnCategory>(column?.category ?? "todo")
  const [wipLimit, setWipLimit] = useState(column?.wip_limit ? String(column.wip_limit) : "")

  const payload = {
    board_column: {
      name,
      category,
      wip_limit: wipLimit.trim() === "" ? null : Number(wipLimit),
    },
  }

  const submit = () => {
    if (!name.trim()) return

    const done = { preserveScroll: true, onSuccess: () => onOpenChange(false) }

    if (column) {
      router.patch(`/boards/${boardKey}/columns/${column.id}`, payload, done)
    } else {
      router.post(`/boards/${boardKey}/columns`, payload, done)
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{column ? `Edit ${column.name}` : "Add column"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="column_name">Name</Label>
          <Input id="column_name" value={name} autoFocus onChange={(event) => setName(event.target.value)} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="column_category">Stage</Label>
          <Select value={category} onValueChange={(value) => setCategory(value as ColumnCategory)}>
            <SelectTrigger id="column_category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="column_wip">Work in progress limit</Label>
          <Input
            id="column_wip"
            type="number"
            min="1"
            value={wipLimit}
            placeholder="No limit"
            onChange={(event) => setWipLimit(event.target.value)}
          />
        </div>

        <DialogFooter className="sm:justify-between">
          {column ? (
            <Button
              variant="ghost"
              className="text-destructive"
              onClick={() =>
                router.delete(`/boards/${boardKey}/columns/${column.id}`, {
                  preserveScroll: true,
                  onSuccess: () => onOpenChange(false),
                })
              }
            >
              Delete column
            </Button>
          ) : (
            <span />
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!name.trim()}>
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
